import React, { useRef, useState, useEffect } from "react";
import axios from "../Axios/Axios";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaUpload,
  FaCamera,
  FaTimes,
  FaPlus,
  FaTrash,
  FaCheck,
  FaRegDotCircle,
  FaTimesCircle,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

export default function AddCustomer() {
  const [image, setImage] = useState(null);
  const [file, setFile] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  const [licenses, setLicenses] = useState([]);
  const [licenseInput, setLicenseInput] = useState("");
  const [expiryDateInput, setExpiryDateInput] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    cnic: "",
    registerDate: "",
    address: "",
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (showCamera) {
      async function startCamera() {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        } catch (err) {
          alert("Camera access denied or not available.");
          setShowCamera(false);
        }
      }
      startCamera();
    } else {
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
    }
  }, [showCamera]);

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL("image/png");
    setImage(dataUrl);

    canvas.toBlob((blob) => {
      const capturedFile = new File([blob], "captured.png", {
        type: "image/png",
      });
      setFile(capturedFile);
    });

    setShowCamera(false);
  };

  const removeImage = () => {
    setImage(null);
    setFile(null);
  };

  const handleUpload = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setImage(URL.createObjectURL(selected));
      setFile(selected);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "cnic") {
      // 1. Remove all non-digit characters
      let digits = value.replace(/\D/g, "");

      // 2. Apply CNIC format: 00000-0000000-0
      if (digits.length > 5 && digits.length <= 12) {
        digits = digits.slice(0, 5) + "-" + digits.slice(5);
      } else if (digits.length > 12) {
        digits =
          digits.slice(0, 5) +
          "-" +
          digits.slice(5, 12) +
          "-" +
          digits.slice(12, 13);
      }

      setFormData((prev) => ({ ...prev, cnic: digits }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // if (!file) {
    //   alert("Please upload or capture an image first.");
    //   return;
    // }

    if (licenses.length === 0) {
      alert("Please add at least one license with an expiry date.");
      return;
    }

    const hasInvalidLicense = licenses.some(
      (lic) => !lic.licenseNumber || !lic.expiryDate
    );
    if (hasInvalidLicense) {
      alert("All licenses must have both license number and expiry date.");
      return;
    }

    setLoading(true); // ⬅️ Start loading

    const token = localStorage.getItem("mughalsLoggedIn");

    const form = new FormData();
    form.append("image", file);
    form.append("name", formData.name);
    form.append("NIC", formData.cnic);
    form.append("registerDate", formData.registerDate);
    form.append("address", formData.address);
    form.append(
      "licenses",
      JSON.stringify(
        licenses.map((lic) => ({
          licenseNumber: lic.licenseNumber,
          LicenseExpiryDate: lic.expiryDate,
        }))
      )
    );

    try {
      const response = await axios.post("/api/customers/add", form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      // alert("Customer added successfully!");
      setFormData({ name: "", cnic: "", registerDate: "", address: "" });
      setLicenses([]);
      setLicenseInput("");
      removeImage();

      Swal.fire({
        title: "Customer added successfully!",
        text: "Do you want to go to Assign Material Page?",
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        confirmButtonColor: "#6fb104",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/assignmaterial");
        }
      });
    } catch (error) {
      console.error("Error adding customer:", error);

      const errorMessage =
        error.response?.data?.error ||
        "An unexpected error occurred. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Failed to Add Customer",
        text: errorMessage,
        confirmButtonColor: "#6fb104",
      });
    } finally {
      setLoading(false); // ⬅️ Stop loading
    }
  };

  return (
    <div className="bg-light">
      {!showCamera && (
        <div className="p-4">
          <h2
            className="text-start text-uppercase mb-4"
            style={{ color: "#6fb104" }}
          >
            Add Customer
          </h2>

          <div className="row mb-4 gx-2 gy-2">
            <div className="col-12 col-md-6">
              <label
                className="w-100 btn text-white fs-5 d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: "#6fb104" }}
              >
                <FaUpload /> Upload Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  hidden
                />
              </label>
            </div>
            <div className="col-12 col-md-6">
              <button
                type="button"
                className="btn w-100 text-white fs-5 d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: "#6fb104" }}
                onClick={() => setShowCamera(true)}
              >
                <FaCamera /> Capture Image
              </button>
            </div>
          </div>

          {image && (
            <div
              className="position-relative mb-4"
              style={{ maxWidth: "500px", margin: "auto" }}
            >
              <img
                src={image}
                alt="preview"
                className="img-fluid rounded border shadow"
                style={{
                  objectFit: "cover",
                  aspectRatio: "4 / 3",
                  width: "100%",
                }}
              />
              <button
                onClick={removeImage}
                className="btn position-absolute top-0 end-0 m-2 text-white"
                style={{ backgroundColor: "#6fb104" }}
              >
                <FaTimes />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="mb-3 col-md-6">
                <label
                  htmlFor="name"
                  className="form-label fw-semibold text-dark"
                >
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  className="form-control p-2"
                  placeholder="Enter Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3 col-md-6">
                <label
                  htmlFor="cnic"
                  className="form-label fw-semibold text-dark"
                >
                  CNIC Number
                </label>
                <input
                  id="cnic"
                  name="cnic"
                  className="form-control p-2"
                  placeholder="00000-0000000-0"
                  value={formData.cnic}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="row">
              <div className="mb-3 col-md-6">
                <label
                  htmlFor="registerDate"
                  className="form-label fw-semibold text-dark"
                >
                  Registration Date
                </label>
                <input
                  id="registerDate"
                  name="registerDate"
                  type="date"
                  className="form-control p-2"
                  value={formData.registerDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3 col-md-6">
                <label
                  htmlFor="address"
                  className="form-label fw-semibold text-dark"
                >
                  Address
                </label>
                <input
                  id="address"
                  name="address"
                  className="form-control p-2"
                  placeholder="Enter Address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <div className="row align-items-center">
                <div className="col-md-5 mb-2">
                  <label className="form-label fw-semibold text-dark">
                    License Details
                  </label>

                  <input
                    type="text"
                    className="form-control p-2"
                    placeholder="Enter License Number"
                    value={licenseInput}
                    onChange={(e) => setLicenseInput(e.target.value)}
                  />
                </div>

                <div className="col-md-5 mb-2">
                  <label className="form-label fw-semibold text-dark">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    className="form-control p-2"
                    placeholder="Enter Expiry Date"
                    value={expiryDateInput}
                    onChange={(e) => setExpiryDateInput(e.target.value)}
                  />
                </div>

                <div className="col-md-2">
                  <button
                    type="button"
                    className="btn w-100 text-white d-flex mt-3 align-items-center justify-content-center gap-2 p-2"
                    style={{ backgroundColor: "#6fb104" }}
                    onClick={() => {
                      if (
                        licenseInput.trim() !== "" &&
                        expiryDateInput !== "" &&
                        licenses.length < 5
                      ) {
                        setLicenses([
                          ...licenses,
                          {
                            licenseNumber: licenseInput.trim(),
                            expiryDate: expiryDateInput,
                          },
                        ]);
                        setLicenseInput("");
                        setExpiryDateInput("");
                      }
                    }}
                  >
                    <FaPlus /> Add
                  </button>
                </div>
              </div>
            </div>

            <ul className="mb-3 list-unstyled">
              {licenses.map((lic, idx) => (
                <li
                  key={idx}
                  className="d-flex align-items-center justify-content-between mb-2 p-2 rounded bg-light border"
                >
                  <span>
                    {lic.licenseNumber} - Exp: {lic.expiryDate}
                  </span>
                  <button
                    onClick={() =>
                      setLicenses(licenses.filter((_, i) => i !== idx))
                    }
                    className="btn btn-sm text-white"
                    style={{ backgroundColor: "#6fb104" }}
                    title="Remove License"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
            </ul>

            <button
              type="submit"
              className="btn  text-white px-5 d-flex align-items-center justify-content-center gap-2 mt-3"
              style={{ backgroundColor: "#6fb104" }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      )}

      {showCamera && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-dark"
          style={{ zIndex: 1050 }}
        >
          <video
            ref={videoRef}
            className="w-100 h-100"
            style={{
              objectFit: "cover",
              maxHeight: "100vh",
              maxWidth: "100vw",
            }}
            autoPlay
            playsInline
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <div
            className="d-flex justify-content-center gap-3 position-absolute"
            style={{ bottom: "20px" }}
          >
            <button
              className="btn text-white d-flex align-items-center gap-2"
              style={{ backgroundColor: "#6fb104" }}
              onClick={captureImage}
            >
              <FaRegDotCircle /> Capture
            </button>
            <button
              className="btn text-white d-flex align-items-center gap-2"
              style={{ backgroundColor: "#6fb104" }}
              onClick={() => setShowCamera(false)}
            >
              <FaTimesCircle /> Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
